import { useCurrentUserQuery } from "core/gateways/user/user.hooks";
import { useCallback } from "react";

import { Permission } from "../types/permission.enum";

export type PermissionsSpec = Permission | Permission[];
export type PermissionsOperator = "and" | "or";

type PermissionsChecker = (
  permission?: PermissionsSpec,
  operator?: PermissionsOperator,
  customPermissionsCheck?: (userPermissions: Permission[]) => boolean
) => boolean;

export const usePermissionsChecker = (): PermissionsChecker => {
  const { data: user } = useCurrentUserQuery();
  const userPermissions = user?.permissions;

  return useCallback(
    (permission, operator = "and", customPermissionsCheck) => {
      if (!userPermissions?.length) return false;

      if (customPermissionsCheck) {
        return customPermissionsCheck(userPermissions);
      }

      if (!permission) return true;
      if (Array.isArray(permission) && !permission.length) return true;

      const hasPermission = (permission: Permission) =>
        userPermissions.includes(permission);

      if (typeof permission === "string") return hasPermission(permission);

      return operator === "or"
        ? permission.some(hasPermission)
        : permission.every(hasPermission);
    },
    [userPermissions]
  );
};
